import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BBox, Position } from 'geojson';
import type { Layer } from '@deck.gl/core';
import { PolygonLayer } from '@deck.gl/layers';
import { useAgentsData } from '../../data/useAgentsData';
import { useTripsData } from '../../data/useTripsData';
import { useAggregationConfig } from '../useAggregationConfig';
import { useMapColorPalette } from '../useMapColorPalette';
import { useLegendConfig } from '../useLegendConfig';
import { useGridHeatmapConfig } from '../useGridHeatmapConfig';
import { useMetaData } from '../../data/useMetaData';
import { useThreeDMode } from '../useThreeDMode';
import { useMapVisibility } from '../useMapVisibility';
import { toMeshSize, getMeshCodeWithBounds } from '../../../utils/mesh';
import {
  bordersToRanges,
  calcBins,
  calcPercentile,
} from '../../../utils/statistics';
import { alpha, type RgbColor } from '../../../utils/color';
import {
  formatCount,
  formatDistance,
  formatDuration,
} from '../../../utils/format';
import type { Legend } from '../../../types/common';

type Grid = {
  bbox: BBox;
  count: number;
  value: number;
  tooltip: string;
};

type UseGridHeatmapLayer = {
  gridHeatmapLayer: Layer;
  gridHeatmapLegends: Legend[];
};

const useGridHeatmapLayer = (): UseGridHeatmapLayer => {
  const { t } = useTranslation();
  const { agents } = useAgentsData();
  const { trips } = useTripsData();
  const { isAggregationVisible } = useMapVisibility();
  const { aggregationConfig } = useAggregationConfig();
  const { scaleType, scaleSize, customRange } = useLegendConfig();
  const { gridSize } = useGridHeatmapConfig();
  const { currentDataMeta } = useMetaData();
  const { isThreeDMode } = useThreeDMode();
  const { getRgbColors, mapColorOpacity } = useMapColorPalette();

  const isCount = ![
    'traveled_time',
    'activity_time',
    'traveled_distance',
  ].includes(aggregationConfig?.target ?? '');
  const viewMagnification = 1 / Number(currentDataMeta?.reduction_factor ?? 1);

  const data = useMemo((): Grid[] => {
    const formatValue = (grid: Omit<Grid, 'tooltip'>): string => {
      switch (aggregationConfig?.target) {
        case 'traveled_time':
        case 'activity_time':
          return formatDuration(grid.value / grid.count);
        case 'traveled_distance':
          return formatDistance(grid.value / grid.count);
        default:
          return formatCount(grid.value, t('unit.agent'));
      }
    };
    const meshSize = toMeshSize(gridSize);

    // データを1件ずつ処理しながらグリッドに集計していく(メモリ効率のためにforとMapを使う)
    const grids = new Map<string, Omit<Grid, 'tooltip'>>();
    if (aggregationConfig?.target === 'home') {
      for (const agent of agents) {
        if (agent.home_lon && agent.home_lat) {
          const { meshCode, bbox } = getMeshCodeWithBounds(
            { lon: agent.home_lon, lat: agent.home_lat },
            meshSize
          );
          grids.set(meshCode, {
            bbox,
            count: (grids.get(meshCode)?.count ?? 0) + 1,
            value: (grids.get(meshCode)?.value ?? 0) + viewMagnification,
          });
        }
      }
    } else if (aggregationConfig?.target === 'work') {
      for (const agent of agents) {
        if (agent.work_lon && agent.work_lat) {
          const { meshCode, bbox } = getMeshCodeWithBounds(
            { lon: agent.work_lon, lat: agent.work_lat },
            meshSize
          );
          grids.set(meshCode, {
            bbox,
            count: (grids.get(meshCode)?.count ?? 0) + 1,
            value: (grids.get(meshCode)?.value ?? 0) + viewMagnification,
          });
        }
      }
    } else if (
      aggregationConfig?.target === 'trip_start' ||
      aggregationConfig?.target === 'stop_by' ||
      aggregationConfig?.subTarget === 'trip_start'
    ) {
      for (const trip of trips) {
        const { meshCode, bbox } = getMeshCodeWithBounds(
          { lon: trip.start_lon, lat: trip.start_lat },
          meshSize
        );
        const value =
          aggregationConfig?.target === 'traveled_distance'
            ? Number(trip.traveled_distance)
            : aggregationConfig?.target === 'traveled_time'
              ? Number(trip.trav_time_seconds)
              : aggregationConfig?.target === 'activity_time'
                ? Number(trip.activity_time_seconds)
                : 1 * viewMagnification;
        grids.set(meshCode, {
          bbox,
          count: (grids.get(meshCode)?.count ?? 0) + 1,
          value: (grids.get(meshCode)?.value ?? 0) + value,
        });
      }
    } else if (
      aggregationConfig?.target === 'trip_end' ||
      aggregationConfig?.subTarget === 'trip_end'
    ) {
      for (const trip of trips) {
        const { meshCode, bbox } = getMeshCodeWithBounds(
          { lon: trip.end_lon, lat: trip.end_lat },
          meshSize
        );
        const value =
          aggregationConfig?.target === 'traveled_distance'
            ? Number(trip.traveled_distance)
            : aggregationConfig?.target === 'traveled_time'
              ? Number(trip.trav_time_seconds)
              : aggregationConfig?.target === 'activity_time'
                ? Number(trip.activity_time_seconds)
                : 1 * viewMagnification;
        grids.set(meshCode, {
          bbox,
          count: (grids.get(meshCode)?.count ?? 0) + 1,
          value: (grids.get(meshCode)?.value ?? 0) + value,
        });
      }
    }

    return Array.from(
      grids,
      ([meshCode, grid]: [string, Omit<Grid, 'tooltip'>]): Grid => {
        return {
          ...grid,
          tooltip: `${meshCode}: ${formatValue(grid)}`,
        };
      }
    );
  }, [
    agents,
    trips,
    aggregationConfig?.target,
    aggregationConfig?.subTarget,
    gridSize,
    viewMagnification,
    t,
  ]);

  const legends = useMemo((): Legend[] => {
    const gridValues = data.map((grid: Grid): number => {
      return isCount ? grid.value : grid.value / grid.count;
    });
    const ranges =
      scaleType === 'custom'
        ? customRange
        : scaleType === 'quantize'
          ? bordersToRanges(calcBins(gridValues, scaleSize))
          : bordersToRanges(calcPercentile(gridValues, scaleSize));

    return getRgbColors(ranges.length).map(
      (color: RgbColor, index: number): Legend => {
        return {
          color: alpha(color, mapColorOpacity),
          range: ranges[index],
        };
      }
    );
  }, [
    data,
    scaleType,
    scaleSize,
    customRange,
    getRgbColors,
    mapColorOpacity,
    isCount,
  ]);

  const maxValue = legends[legends.length - 1]?.range[1] ?? 1;

  const gridHeatmapLayer = new PolygonLayer<Grid>({
    id: 'grid-heatmap-layer',
    data: data,
    getPolygon: (grid: Grid): Position[] => {
      return [
        [grid.bbox[0], grid.bbox[1]],
        [grid.bbox[2], grid.bbox[1]],
        [grid.bbox[2], grid.bbox[3]],
        [grid.bbox[0], grid.bbox[3]],
        [grid.bbox[0], grid.bbox[1]],
      ];
    },
    getElevation: (grid: Grid): number => {
      return grid.value / maxValue;
    },
    elevationScale: 5000,
    getFillColor: (grid: Grid): RgbColor => {
      return (
        legends.find((legend): boolean => {
          return legend.range[0] <= grid.value && grid.value <= legend.range[1];
        })?.color ?? [0, 0, 0, 0]
      );
    },
    filled: true,
    stroked: false,
    extruded: isThreeDMode,
    pickable: true,
    visible: isAggregationVisible && aggregationConfig?.type === 'grid_heatmap',
    updateTriggers: {
      getFillColor: [legends],
      getElevation: [maxValue],
    },
  });

  return { gridHeatmapLayer: gridHeatmapLayer, gridHeatmapLegends: legends };
};

export { useGridHeatmapLayer };
